import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormLabel,
  SxProps,
} from '@mui/material';
import { t } from 'i18next';
import { BLUE_GRAY_200, PRIMARY } from '../../constants/Colors';
import { Fragment, useEffect, useState } from 'react';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const filter = createFilterOptions<any>();

interface BakeasySelectAutoCompleteProps {
  key?: any;
  inputValue?: string;
  options?: any;
  value?: string;
  label?: string;
  disabled?: boolean;
  multiple?: boolean;
  required?: boolean;
  values?: string[] | undefined;
  onChange: (newValue: any) => void;
  onAddClick?: (props: any) => void;
}

function sleep(duration: number): Promise<void> {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, duration);
  });
}

export const autoCompleteSxProps: SxProps = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: BLUE_GRAY_200, borderRadius: 2 },
    '&:hover fieldset': { borderColor: BLUE_GRAY_200 },
    '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 1.5 },
  },
  '& .MuiInputBase-input::placeholder': { fontSize: '0.9rem' },
};

export function BakeasyAutoCompleteCategoryA(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(props?.options ?? []);
  const loading = open && options?.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3 / 2);

      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('CATEGORY')}
          {' *'}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            return option?.categoryName === value?.categoryName;
          }}
          getOptionLabel={(option: any) => {
            return option?.categoryName;
          }}
          options={options}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_CATEGORY')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            if (
              (typeof value === 'string' && value?.length > 2) ||
              value?.length === 0
            ) {
              props?.onChange(value);
            }
          }}
          onChange={(event, newValue) => {
            if (typeof newValue === 'object') {
              props?.onChange(newValue);
            }
          }}
        />
      </FormControl>
    </Fragment>
  );
}

export function BakeasyAutoCompleteSubCategoryA(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(props?.options);
  const loading = open && options?.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3 / 2);
      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <FormControl sx={{ minWidth: 120 }} fullWidth={true} disabled={false}>
      <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
        {props?.label ? t(props.label) : t('SUBCATEGORY')}
        {' *'}
      </FormLabel>
      <Autocomplete
        size="small"
        value={props.value || null}
        disabled={props?.disabled ?? false}
        sx={autoCompleteSxProps}
        id="asynchronous-demo"
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        isOptionEqualToValue={(option: any, value: any) => {
          if (typeof option === 'string') {
            return true;
          }
          return option?.subCategoryName === value?.subCategoryName;
        }}
        getOptionLabel={(option: any) => {
          if (typeof option === 'string') {
            return option;
          }
          return option?.subCategoryName || 'No data';
          // return option?.subCategoryName;
        }}
        options={options}
        loading={loading}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder={t('SELECT_SUB_CATEGORY')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          );
        }}
        onInputChange={(event: any, value: any) => {
          if (
            (typeof value === 'string' && value?.length > 2) ||
            value?.length === 0
          ) {
            props?.onChange(value);
          }
        }}
        onChange={(event, newValue) => {
          if (typeof newValue === 'object') {
            props?.onChange(newValue);
          }
        }}
      />
    </FormControl>
  );
}

const icon = <CheckBoxOutlineBlankIcon />;
const checkedIcon = <CheckBoxIcon />;
export function BakeasyAutoCompleteMessagesTo(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(props?.options);
  const loading = open && options?.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3 / 2);

      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props?.label) : t('TO')}
          {' *'}
        </FormLabel>
        <Autocomplete
          multiple={props?.multiple ?? false}
          size="small"
          value={props?.multiple ? props?.values : props?.value}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            return option?.supplierName === value?.supplierName;
          }}
          getOptionLabel={(option: any) => {
            if (typeof option === 'object') {
              return option?.supplierName;
            }

            return option;
          }}
          options={options}
          loading={loading}
          disableCloseOnSelect
          renderOption={(props, option: any, { selected }) => {
            return (
              <li {...props} style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ padding: 0 }}
                  checked={selected}
                  size="small"
                />
                <span style={{ marginLeft: '8px' }}>
                  {option?.supplierName}
                </span>
              </li>
            );
          }}
          // renderInput={(params: any) => {
          //   return (
          //     <TextField
          //       {...params}
          //       placeholder={t('SELECT_TO')}
          //       InputProps={{
          //         ...params.InputProps,
          //         endAdornment: (
          //           <Fragment>
          //             {loading ? <CircularProgress color="inherit" size={20} /> : null}
          //             {params.InputProps.endAdornment}
          //           </Fragment>
          //         )
          //       }}
          //     />
          //   );
          //   //}
          // }}
          renderInput={(params) => (
            <TextField {...params} placeholder={t('SELECT_TO')} />
          )}
          onInputChange={(event: any, value: any) => {
            if (typeof value === 'string' && value?.length > 2) {
              props?.onChange(value);
            }
          }}
          onChange={(event, newValue) => {
            if (typeof newValue === 'object') {
              props?.onChange(newValue);
            }
          }}
        />
      </FormControl>
    </Fragment>
  );
}

export function BakeasyAutoCompleteIngredient(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(null);
  const loading = open && options.length === 0;
  let newIngredient: any = { id: 0, name: 'Add New Ingredient' };

  function setOption() {
    let newOptions: {}[] =
      props?.options && props?.options?.length > 0 ? props?.options : [];
    setOptions(newOptions);
  }

  useEffect(() => {
    setOption();
  }, [props?.options]);

  return (
    <Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('INGREDIENT')}
          {' *'}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props?.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            if (!filtered?.includes(newIngredient)) {
              filtered.push(newIngredient);
            }

            return filtered;
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            return option?.ingredientName === value?.ingredientName;
          }}
          getOptionLabel={(option: any) => {
            if (option?.ingredientName) return option?.ingredientName;
            if (option?.name) {
              return option?.name;
            }
          }}
          options={options}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_INGREDIENT')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}
              />
            );
            //}
          }}
          onInputChange={(event: any, value: any) => {
            if (typeof value === 'string' && value?.length > 2) {
            }
            if (typeof value === 'string' && value?.startsWith('Add ')) {
            }
          }}
          onChange={(event, newValue: any) => {
            if (
              typeof newValue === 'object' &&
              newValue?.id === 0 &&
              newValue?.name === 'Add New Ingredient'
            ) {
              if (props?.onAddClick) props?.onAddClick(newValue);
              return;
            }

            if (typeof newValue === 'object') {
              props?.onChange(newValue);
            }
          }}
        />
      </FormControl>
    </Fragment>
  );
}

export function BakeasyAutoCompleteProduct(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(props?.options);
  const loading = open && options.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3 / 2);
      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <FormControl sx={{ minWidth: 120 }} fullWidth={true} disabled={false}>
      <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
        {props?.label ? t(props.label) : t('PRODUCT')}
        {' *'}
      </FormLabel>
      <Autocomplete
        size="small"
        value={props.value || null}
        disabled={props?.disabled ?? false}
        sx={autoCompleteSxProps}
        id="asynchronous-demo"
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        isOptionEqualToValue={(option: any, value: any) => {
          if (typeof option === 'string') {
            return true;
          }
          return option?.productName === value?.productName;
        }}
        getOptionLabel={(option: any) => {
          if (typeof option === 'string') {
            return option;
          }
          if (typeof option === 'object') {
            return option?.productName || 'No data';
            //  return option?.productName ;
          }
          return '';
        }}
        options={options}
        loading={loading}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder={t('SELECT_PRODUCT')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          );
        }}
        onInputChange={(event: any, value: any) => {
          if (typeof value === 'string' && value?.length > 2) {
            props?.onChange(value);
          }

          if (typeof value === 'string' && value?.length === 0) {
            props?.onChange(value);
          }
        }}
        onChange={(event, newValue) => {
          if (typeof newValue === 'object') {
            props?.onChange(newValue);
          }
        }}
      />
    </FormControl>
  );
}

export function BakeasyAutoCompleteCategory(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(
    props?.options ? props?.options : []
  );
  const loading = open && options?.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3);

      if (active) {
        setOptions(props?.options ? props?.options : []);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('CATEGORY')}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            if (typeof option === 'string') {
              return true;
            }
            return option?.cityName === value?.cityName;
          }}
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') {
              return option;
            }
            return option?.cityName;
          }}
          options={options ?? []}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_CATEGORY')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            props?.onChange(value);
          }}
          onChange={(event, newValue) => {
            props?.onChange(newValue);
          }}
        />
      </FormControl>
    </Fragment>
  );
}

export function BakeasyAutoCompleteSubCategory(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(
    props?.options ? props?.options : []
  );
  const loading = open && options?.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3);

      if (active) {
        setOptions(props?.options ? props?.options : []);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('SUB_CATEGORY')}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            if (typeof option === 'string') {
              return true;
            }

            return option?.cityName === value?.cityName;
          }}
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') {
              return option;
            }
            return option?.cityName;
          }}
          options={options ?? []}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_SUB_CATEGORY')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            props?.onChange(value);
          }}
          onChange={(event, newValue) => {
            props?.onChange(newValue);
          }}
        />
      </FormControl>
    </Fragment>
  );
}

export function BakeasyAutoCompleteCountry(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(props?.options ?? []);
  const loading = open && options?.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3 / 2);

      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('COUNTRY')}
          {props?.required ? ' *' : ''}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            return option?.countryName === value?.countryName;
          }}
          getOptionLabel={(option: any) => {
            if (!option?.countryName) return '';
            const key = `${option.countryName.toUpperCase().replace(/\s+/g, '_')}`;
            const translation = t(key);
            return translation === key ? option.countryName : translation;
          }}
          options={options}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_COUNTRY')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            if (typeof value === 'string' && value?.length > 2) {
              props?.onChange(value);
            }
          }}
          onChange={(event, newValue) => {
            if (typeof newValue === 'object') {
              props?.onChange(newValue);
            }
          }}
        />
      </FormControl>
    </Fragment>
  );
}

export function BakeasyAutoCompleteState(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(props?.options);
  const loading = open && options?.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3 / 2);
      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <FormControl sx={{ minWidth: 120 }} fullWidth={true} disabled={false}>
      <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
        {props?.label ? t(props.label) : t('STATE')}
        {/* {' *'} */}
        {props?.required ? ' *' : ''}
      </FormLabel>
      <Autocomplete
        size="small"
        value={props.value || null}
        disabled={props?.disabled ?? false}
        sx={autoCompleteSxProps}
        id="asynchronous-demo"
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        isOptionEqualToValue={(option: any, value: any) => {
          return option?.stateName === value?.stateName;
        }}
        // getOptionLabel={(option: any) => {
        //   return option?.stateName;
        // }}
        // options={options}

        getOptionLabel={(option: any) => {
          if (!option?.stateName) return '';
          const key = `${option.stateName.toUpperCase().replace(/\s+/g, '_')}`;
          const translation = t(key);
          return translation === key ? option.stateName : translation;
        }}
        options={options}
        loading={loading}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder={t('SELECT_STATE')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          );
        }}
        onInputChange={(event: any, value: any) => {
          if (typeof value === 'string' && value?.length > 2) {
            props?.onChange(value);
          }
        }}
        onChange={(event, newValue) => {
          if (typeof newValue === 'object') {
            props?.onChange(newValue);
          }
        }}
      />
    </FormControl>
  );
}
